import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  Button
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import api from '../../../services/api';

const AportesSection = ({ emprestimoId }) => {
  const [aportes, setAportes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const loadAportes = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/emprestimos/${emprestimoId}/aportes-detalhados`);
      setAportes(response.data);
    } catch (error) {
      console.error('Erro ao carregar aportes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (emprestimoId) {
      loadAportes();
    }
  }, [emprestimoId]);

  const handleToggleExpanded = () => {
    setExpanded(!expanded);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ativo': return 'success';
      case 'processando': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box 
        display="flex" 
        alignItems="center" 
        justifyContent="space-between"
        sx={{ 
          p: 2, 
          bgcolor: '#f8f9fa', 
          borderRadius: 2, 
          border: '1px solid #e9ecef',
          cursor: 'pointer'
        }}
        onClick={handleToggleExpanded}
      >
        <Box display="flex" alignItems="center">
          <MoneyIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle1" fontWeight="bold">
            Aportes Realizados
          </Typography>
          <Chip 
            label={aportes.length} 
            size="small" 
            color="primary" 
            sx={{ ml: 1 }}
          />
        </Box>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ mt: 2 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <Typography>Carregando aportes...</Typography>
            </Box>
          ) : aportes.length === 0 ? (
            <Box display="flex" justifyContent="center" p={3}>
              <Typography color="text.secondary">
                Nenhum aporte realizado ainda
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell><strong>#</strong></TableCell>
                    <TableCell><strong>Data</strong></TableCell>
                    <TableCell><strong>Valor Original</strong></TableCell>
                    <TableCell><strong>Juros</strong></TableCell>
                    <TableCell><strong>Valor Total</strong></TableCell>
                    <TableCell><strong>Taxa</strong></TableCell>
                    <TableCell><strong>Parcelas</strong></TableCell>
                    <TableCell><strong>Valor/Parcela</strong></TableCell>
                    <TableCell><strong>Ações</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {aportes.map((aporte, index) => (
                    <TableRow key={aporte.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          #{aporte.numero_aporte}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(aporte.data_aporte)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="primary">
                          {formatCurrency(aporte.valor_aporte_original)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="secondary">
                          {formatCurrency(aporte.valor_juros_aporte)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(aporte.valor_aporte_total)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`${aporte.taxa_juros_aplicada}%`}
                          size="small"
                          color="info"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {aporte.parcelas_afetadas} parcelas
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" color="primary">
                            {formatCurrency(aporte.valor_por_parcela)}
                          </Typography>
                          <Typography variant="caption" color="secondary">
                            +{formatCurrency(aporte.juros_por_parcela)} juros
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Ver detalhes">
                          <IconButton size="small" color="primary">
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default AportesSection;
